import { LineItem } from '../types'

export const DEFAULT_TAX_RATE = 0.06 // Michigan 6%
export const STRIPE_FEE_PERCENT = 0.029
export const STRIPE_FEE_FIXED = 0.3

export function calculateProfitMargin(
  price: number,
  jobCost?: number,
): number | undefined {
  if (jobCost === undefined || jobCost === null || price === 0) return undefined
  return (price - jobCost) / price
}

export function calculateLineItemTotal(
  quantity: number,
  price: number,
): number {
  return quantity * price
}

export function calculateSubtotal(lineItems: LineItem[]): number {
  return lineItems.reduce((sum, item) => sum + item.quantity * item.price, 0)
}

export function calculateTaxableAmount(lineItems: LineItem[]): number {
  return lineItems
    .filter((item) => item.is_taxable)
    .reduce((sum, item) => sum + item.quantity * item.price, 0)
}

export function calculateTaxAmount(
  lineItems: LineItem[],
  taxRate: number = DEFAULT_TAX_RATE,
): number {
  const taxableAmount = calculateTaxableAmount(lineItems)
  return Math.round(taxableAmount * taxRate * 100) / 100
}

export function calculateInvoiceTotal(
  subtotal: number,
  taxAmount: number,
  shippingCharge: number = 0,
  discount: number = 0,
): number {
  return (
    Math.round((subtotal + shippingCharge + taxAmount - discount) * 100) / 100
  )
}

export function calculateStripeFee(amount: number): number {
  return (
    Math.round((amount * STRIPE_FEE_PERCENT + STRIPE_FEE_FIXED) * 100) / 100
  )
}

export function calculateNetAmount(amount: number, stripeFee?: number): number {
  if (stripeFee === undefined) return amount
  return Math.round((amount - stripeFee) * 100) / 100
}

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)
}

export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

export function generateId(prefix: string = ''): string {
  return `${prefix}${Date.now().toString(36)}${Math.random().toString(36).substr(2, 9)}`
}

export function getCycleName(cycleDays: number): string {
  switch (cycleDays) {
    case 7:
      return 'Weekly'
    case 14:
      return 'Bi-weekly'
    case 30:
      return 'Monthly'
    case 60:
      return 'Bi-monthly'
    case 90:
      return 'Quarterly'
    case 180:
      return 'Semi-annually'
    case 365:
      return 'Annually'
    default:
      return `Every ${cycleDays} days`
  }
}

export function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

export function isOverdue(date: Date): boolean {
  return new Date(date) < new Date()
}
